import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, Shield, Package, Settings, Tag, Megaphone, FileText, FolderCheck, Award, Target, BarChart3, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const MyntraAccountManagement = () => {
  const brandApprovalServices = [
    'Guiding through Myntra brand approval requirements',
    'Submitting necessary brand documentation',
    'Ensuring compliance with Myntra policies',
    'Resolving approval-related issues'
  ];

  const listingCreationServices = [
    'Creating new product listings',
    'Category and sub-category mapping',
    'Product title and attribute updates',
    'Image compliance checks',
    'Ensuring listings follow Myntra catalog standards'
  ];

  const listingOptimizationServices = [
    'Optimizing product titles and descriptions',
    'Updating product attributes and specifications',
    'Improving catalog information accuracy',
    'Enhancing product presentation'
  ];

  const promotionServices = [
    'Adding discount deals and promotional offers',
    'Creating coupon-based offers',
    'Managing Myntra event offers and sale campaigns',
    'Adding pricing discounts on MRP to attract customers'
  ];

  const advertisingServices = [
    'Campaign setup and management',
    'Selecting products for advertising campaigns',
    'Monitoring campaign budgets and performance',
    'Optimizing campaigns to improve results'
  ];

  const catalogManagementServices = [
    'Selecting the right products for campaigns',
    'Monitoring product performance',
    'Managing catalog updates',
    'Ensuring catalog accuracy'
  ];

  const whyChooseUs = [
    { icon: Shield, title: 'Brand Approval Expertise', description: 'Smooth onboarding with complete documentation support.' },
    { icon: Package, title: 'Professional Listing Management', description: 'Accurate, compliant product listings that perform.' },
    { icon: Settings, title: 'Catalog Optimization', description: 'Enhanced product presentation for better visibility.' },
    { icon: Tag, title: 'Promotional Campaign Management', description: 'Strategic offers that drive sales and conversions.' },
    { icon: Megaphone, title: 'Advertising Management', description: 'Data-driven campaigns that maximize ROI.' },
    { icon: RefreshCw, title: 'Ongoing Account Support', description: 'Continuous monitoring and optimization.' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Myntra Account Management Services | Sell on Myntra India</title>
        <meta name="description" content="Complete Myntra seller account management: brand onboarding, catalog optimization, advertising & performance tracking. Grow your fashion brand on Myntra." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/myntra-account-management-services" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Myntra Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 64, 129, 0.03) 0%, rgba(233, 30, 99, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-pink-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-rose-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Myntra Account <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">Management Services</span>
              </h1>
              <p className="text-lg leading-relaxed text-slate-700 mb-6 max-w-3xl mx-auto">
                Managing a successful brand presence on Myntra requires proper catalog management, listing optimization, pricing strategy, promotional campaigns, and advertising management.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                At Obiquiste Solutions, we provide complete Myntra Account Management Services to help brands manage their accounts efficiently and improve product visibility and sales performance on the platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=myntra-account">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Account Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=myntra-account">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Myntra Expert
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Myntra Brand Approval Assistance */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-pink-100 rounded-xl mb-6">
                <Shield className="w-7 h-7 text-pink-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Myntra Brand Approval Assistance
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Before listing products under a brand name on Myntra, sellers must complete the brand approval process.
              </p>
              <p className="text-lg text-slate-700">
                We help brands complete the approval process smoothly, ensuring all documentation is in order and compliance requirements are met.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Brand Approval Services:</h3>
                <div className="space-y-4">
                  {brandApprovalServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Listing Creation */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Listing Creation Services:</h3>
                <div className="space-y-4">
                  {listingCreationServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <Package className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Listing Creation
              </h2>
              <p className="text-lg text-slate-700">
                We help brands create new product listings according to Myntra catalog guidelines. Our team ensures that all product information is accurate, properly categorized, and compliant with platform standards.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Listing Optimization */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-6">
                <Settings className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Listing Optimization
              </h2>
              <p className="text-lg text-slate-700">
                Optimized product listings help improve product visibility and customer understanding. We enhance your existing listings to ensure they perform better and attract more customers.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Optimization Services:</h3>
                <div className="space-y-4">
                  {listingOptimizationServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Promotions, Discounts & Event Campaigns */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Promotional Services:</h3>
                <div className="space-y-4">
                  {promotionServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-800 text-sm">
                    <strong>Impact:</strong> These promotions help increase product visibility and conversions.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Tag className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Promotions, Discounts & Event Campaigns
              </h2>
              <p className="text-lg text-slate-700">
                Promotional offers play an important role in attracting customers on Myntra. We help brands manage promotional activities to drive sales and increase customer engagement during key shopping events.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Myntra Advertising Management */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-xl mb-6">
                <Megaphone className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Myntra Advertising Management
              </h2>
              <p className="text-lg text-slate-700">
                We help brands manage advertising campaigns on Myntra to improve product visibility and drive targeted traffic to your listings. Our team monitors and optimizes campaigns for maximum results.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Advertising Services:</h3>
                <div className="space-y-4">
                  {advertisingServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Purchase Order (PO) Management */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-6">
              <FileText className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Purchase Order (PO) Management for Campaigns
            </h2>
            <p className="text-lg text-slate-700 mb-6">
              Purchase orders must be raised on time to ensure campaigns run smoothly. We monitor campaigns and raise purchase orders on time so that advertising campaigns continue without interruption.
            </p>
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm inline-block">
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                <span className="text-slate-700 font-medium">Timely PO management ensures uninterrupted campaign performance</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Selection & Catalog Management */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Catalog Management Services:</h3>
                <div className="space-y-4">
                  {catalogManagementServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-100 rounded-xl mb-6">
                <FolderCheck className="w-7 h-7 text-teal-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Selection & Catalog Management
              </h2>
              <p className="text-lg text-slate-700">
                Choosing the right products for promotions and campaigns is important for better performance. We help identify high-potential products and manage your catalog to maximize results.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Complete Myntra Account Management Support
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Obiquiste Solutions provides complete Myntra account management support including brand approvals, listing creation, catalog optimization, promotions, and advertising management to help brands grow on the platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-12 border border-pink-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Grow Your Myntra Business?
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              Let our experts manage your Myntra account and help you achieve better visibility, increased sales, and sustainable growth on the platform.
            </p>
            <Link to="/contact?service=myntra-account">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free Account Audit
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyntraAccountManagement;
